import { defineComponent, ref, onMounted, onUnmounted, watch, defineAsyncComponent } from 'vue'
import type { Ref } from 'vue'
import { useRouter } from 'vue-router'
import { LotteryBallRow } from '@/components/lottery-ball-row'
import { getTodayDateStr, normalizeIssue } from '@/utils/lottery-attr'
import { normalizeExternalUrl } from '@/utils/open-external'
import type { LotteryBall, BallColor } from '@/components/lottery-ball-row'
import { useRegionStore } from '@/stores/region-store'
import type { LotteryType } from '@/stores/region-store'
export type { LotteryType } from '@/stores/region-store'
import { getApiV1ExternalLinksList, getApiV1KeywordRankings } from '@/api/sdk.gen'
import type { LiveLotteryData, ExternalLink, KeywordRanking } from '@/api/types.gen'


// ==================== WebSocket 配置常量 ====================

/** WS 断线后的重连等待时间（毫秒） */
const WS_RECONNECT_DELAY_MS = Number(import.meta.env.VITE_WS_RECONNECT_DELAY_MS) || 3000
/** 各彩种的 WebSocket 端点地址 */
const WS_ENDPOINTS: Record<LotteryType, string> = {
  macau: import.meta.env.VITE_WS_ENDPOINTS_MACAU,
  hk: import.meta.env.VITE_WS_ENDPOINTS_HK,
}

/** WS 开奖期间消息超时时间（毫秒）：超过此时间无消息则触发重连 */
const WS_MESSAGE_TIMEOUT_MS = Number(import.meta.env.VITE_WS_MESSAGE_TIMEOUT_MS) || 10 * 1000

console.log('VITE_WS_RECONNECT_DELAY_MS', WS_RECONNECT_DELAY_MS)
console.log('VITE_WS_ENDPOINTS', WS_ENDPOINTS)
console.log('VITE_WS_MESSAGE_TIMEOUT_MS', WS_MESSAGE_TIMEOUT_MS)

// ==================== 调试日志 ====================

function logDebug(area: string, ...args: unknown[]) {
  console.log(`[Lottery][${area}]`, ...args)
}

// ==================== 辅助函数 ====================

// 将 WS 数据映射为 LotteryBall 数组（仅解析号码）。
// 颜色、生肖、五行由 LotteryBallRow 组件的 useAlgorithm 模式通过 lottery-attr.ts 前端计算。
// 开奖直播进行中时，main_numbers / special_number 为中文字符而非数字字符串，
// 此时跳过 parseInt，直接保留原始字符串透传给 BallItem 渲染。
export function parseLiveBalls(data: LiveLotteryData): {
  balls: LotteryBall[]
  specialBall: LotteryBall
} {
  const balls: LotteryBall[] = (data.main_numbers ?? []).map((num) => ({
    number: /^\d+$/.test(num) ? Number.parseInt(num, 10) : num,
    color: 'gray' as BallColor,
    zodiac: '',
    wuxing: '',
  }))
  const specialNum = data.special_number ?? ''
  const specialBall: LotteryBall = {
    number: /^\d+$/.test(specialNum) ? Number.parseInt(specialNum, 10) : specialNum,
    color: 'gray' as BallColor,
    zodiac: '',
    wuxing: '',
  }
  return { balls, specialBall }
}

// "2026/03/10" → "03月10日"
export function formatNextDrawDate(dateStr: string): string {
  const parts = dateStr.split('/')
  return `${parts[1]}月${parts[2]}日`
}

// "2026/03/10" + "21点32分" → Date
// ⚠️ 后端返回的日期/时间均为 UTC+8（北京时间），与浏览器本地时区无关。
// 必须在 ISO 字符串末尾显式附加 +08:00，强制按 UTC+8 解析；
// 否则 new Date("2026-03-10T21:32:00") 会被浏览器视为本地时间，
// 导致非 +8 时区用户（如 UTC+9）的倒计时提前/延后 1 小时归零。
export function parseNextDrawDatetime(dateStr: string, timeStr: string): Date | null {
  if (!dateStr || !timeStr) return null
  // 兼容 "/" 和 "-" 两种日期分隔符
  const dateParts = dateStr.split(/[/-]/)
  if (dateParts.length < 3) {
    logDebug('Countdown', '日期格式异常', dateStr)
    return null
  }
  const [y, m, d] = dateParts

  // 兼容 "21点32分" 和 "21:32" 两种时间格式
  let hour: number
  let minute: number
  if (timeStr.includes('点')) {
    hour = Number.parseInt(timeStr)
    minute = Number.parseInt(timeStr.split('点')[1] ?? '0')
  } else if (timeStr.includes(':')) {
    const timeParts = timeStr.split(':')
    hour = Number.parseInt(timeParts[0] ?? '0')
    minute = Number.parseInt(timeParts[1] ?? '0')
  } else {
    hour = Number.parseInt(timeStr)
    minute = 0
  }

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    logDebug('Countdown', '时间格式异常', timeStr, { hour, minute })
    return null
  }

  const result = new Date(
    `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00+08:00`,
  )
  if (Number.isNaN(result.getTime())) {
    logDebug('Countdown', '解析后日期无效', dateStr, timeStr)
    return null
  }
  return result
}

/**
 * 解析 WebSocket 消息的 k 字段，返回可合并入 LiveLotteryData 的 Partial 对象。
 *
 * 澳彩 k 字段格式（逗号分隔，共13项）：
 *   [0]  当前期信息（如 "2026073"，年份+期号合并）
 *   [1]  正码1 / [2] 正码2 / [3] 正码3 / [4] 正码4 / [5] 正码5 / [6] 正码6
 *   [7]  特码
 *   [8]  下期期信息（如 "2026074"，前4位为年份）
 *   [9]  下期月份 / [10] 下期日 / [11] 下期星期 / [12] 下期时间（如 "21点32分"）
 *
 * 港彩 k 字段格式相同，但 [0] 和 [8] 只有期号（如 "035"），不含年份。
 * 年份需从当前系统日期（UTC+8）推导，并处理跨年边界（12月 → 次年1月）。
 *
 * ⚠️ WS 消息中的 t（可能为刷新间隔毫秒数）和 d（可能为距开奖天数）字段
 *    含义尚未确认，暂时忽略，不写入 LiveLotteryData。
 */
function parseWsKField(k: string, type: LotteryType): Partial<LiveLotteryData> {
  const parts = k.split(',')
  if (parts.length < 13) return {}

  const currentRaw = parts[0] ?? ''
  const nextRaw = parts[8] ?? ''
  const nextMonth = (parts[9] ?? '').padStart(2, '0')
  const nextDay = (parts[10] ?? '').padStart(2, '0')

  // 澳彩：期号含年份（如 "2026073"，长度 > 4）；港彩：期号不含年份（如 "035"）
  let nextYear: string
  if (type === 'macau' && nextRaw.length > 4) {
    nextYear = nextRaw.slice(0, 4)
  } else {
    // 港彩无年份，从系统时钟（UTC+8）推导。
    // 若下期月份为 1 月且当前月份为 12 月，则跨年取下一年。
    const nowUtc8 = new Date(Date.now() + 8 * 60 * 60 * 1000)
    const curYear = nowUtc8.getUTCFullYear()
    const curMonth = nowUtc8.getUTCMonth() + 1 // 1-12
    const nextMonthNum = Number.parseInt(nextMonth)
    nextYear = String(curMonth === 12 && nextMonthNum === 1 ? curYear + 1 : curYear)
  }

  return {
    current_issue:
      type === 'macau' && currentRaw.length > 4
        ? String(Number.parseInt(currentRaw.slice(4)))
        : currentRaw,
    main_numbers: parts.slice(1, 7),
    special_number: parts[7] ?? '',
    next_issue:
      type === 'macau' && nextRaw.length > 4 ? String(Number.parseInt(nextRaw.slice(4))) : nextRaw,
    next_draw_date:
      nextYear && nextMonth && nextDay ? `${nextYear}/${nextMonth}/${nextDay}` : undefined,
    next_draw_weekday: parts[11],
    next_draw_time: parts[12],
  }
}

// 判断当前是否处于开奖直播状态：main_numbers 第一个元素为非数字字符串时说明正在直播
function isDrawingLive(data: LiveLotteryData): boolean {
  const nums = data.main_numbers ?? []
  return nums.length > 0 && !/^\d+$/.test(nums[0] ?? '')
}

/** 距下期开奖不超过 windowMs（开奖时间已过 2 分钟内也算） */
function isNearNextDraw(data: LiveLotteryData, windowMs: number): boolean {
  if (!data.next_draw_date || !data.next_draw_time) return false
  const target = parseNextDrawDatetime(data.next_draw_date, data.next_draw_time)
  if (!target) return false
  const diffMs = target.getTime() - Date.now()
  // 容差 2 分钟：开奖时间已过但可能仍在进行
  return diffMs > -(2 * 60 * 1000) && diffMs <= windowMs
}

/** 号码是否已揭晓为数字（未揭晓时是中文占位符或空串） */
function isRevealedNumber(n: string): boolean {
  return n.length > 0 && /^\d+$/.test(n)
}

/** 判断开奖是否已完成：7个号码（6正码 + 1特码）全为纯数字字符串且非空 */
function isDrawComplete(data: LiveLotteryData): boolean {
  const all = [...(data.main_numbers ?? []), data.special_number ?? '']
  return all.length === 7 && all.every(isRevealedNumber)
}

/** 判断是否应显示直播播放器：正在开奖直播 或 号码部分出完但未全出 */
export function shouldShowLivePlayer(data: LiveLotteryData): boolean {
  if (isDrawingLive(data)) return true
  // 至少有一个号码已出为数字，但未全部出完 → 仍在开奖
  const nums = data.main_numbers ?? []
  if (nums.some((n) => /^\d+$/.test(n)) && !isDrawComplete(data)) return true
  return false
}

// ==================== useWsLiveData Hook ====================

/**
 * 管理港彩/澳彩的 WebSocket 连接。
 *
 * WS 在组件挂载时立即连接。所有开奖数据均来自 WS 消息，
 * 颜色/生肖/五行由前端 lottery-attr.ts 计算。
 *
 * 断线重连窗口：仅在开奖前 10 分钟 或 开奖开始后 10 分钟内断线才重连，
 * 其余时间断线后不再重连。
 */
export function useWsLiveData(
  hkData: Ref<LiveLotteryData | null>,
  aocaiData: Ref<LiveLotteryData | null>,
  hkDrawingMode: Ref<boolean>,
  aocaiDrawingMode: Ref<boolean>,
) {
  const hkWsActive = ref(false)
  const aocaiWsActive = ref(false)
  let hkWs: WebSocket | null = null
  let aocaiWs: WebSocket | null = null
  let hkDrawCompleted = false
  let aocaiDrawCompleted = false
  // 标记是否已进入开奖直播阶段（收到过 k[0] === k[8] 的消息）。
  // 用于区分 PRE_DRAW（上期数据）和 POST_DRAW（当期开奖完成）的 k[0] !== k[8] 消息。
  let hkDrawingStarted = false
  let aocaiDrawingStarted = false
  // 记录进入开奖直播的时间戳，用于判断断线重连窗口
  let hkDrawStartTime = 0
  let aocaiDrawStartTime = 0
  let hkWsReconnectTimer: ReturnType<typeof setTimeout> | undefined
  let aocaiWsReconnectTimer: ReturnType<typeof setTimeout> | undefined
  let hkWsSendTimer: ReturnType<typeof setInterval> | undefined
  let aocaiWsSendTimer: ReturnType<typeof setInterval> | undefined
  let hkLastMessageTime = 0
  let aocaiLastMessageTime = 0
  let hkMessageTimeoutTimer: ReturnType<typeof setInterval> | undefined
  let aocaiMessageTimeoutTimer: ReturnType<typeof setInterval> | undefined

  function closeWs(type: LotteryType) {
    logDebug('WS', '关闭连接', type)
    if (type === 'hk') {
      clearTimeout(hkWsReconnectTimer)
      clearInterval(hkWsSendTimer)
      clearInterval(hkMessageTimeoutTimer)
      if (hkWs) {
        hkWs.onclose = null
        hkWs.close()
        hkWs = null
      }
      hkWsActive.value = false
    } else {
      clearTimeout(aocaiWsReconnectTimer)
      clearInterval(aocaiWsSendTimer)
      clearInterval(aocaiMessageTimeoutTimer)
      if (aocaiWs) {
        aocaiWs.onclose = null
        aocaiWs.close()
        aocaiWs = null
      }
      aocaiWsActive.value = false
    }
  }

  function checkDrawComplete(type: LotteryType) {
    const data = type === 'hk' ? hkData.value : aocaiData.value
    const alreadyCompleted = type === 'hk' ? hkDrawCompleted : aocaiDrawCompleted
    if (!data || alreadyCompleted) return
    if (!isDrawComplete(data)) return

    logDebug('WS', '开奖完成', type)
    if (type === 'hk') hkDrawCompleted = true
    else aocaiDrawCompleted = true
  }

  /** 把解析出的字段合并进对应彩种的数据 */
  function mergeData(type: LotteryType, parsed: Partial<LiveLotteryData>) {
    if (type === 'hk') {
      hkData.value = { ...hkData.value, ...parsed }
    } else {
      aocaiData.value = { ...aocaiData.value, ...parsed }
    }
  }

  /** k[0] !== k[8]：PRE_DRAW 或 POST_DRAW（均为非直播窗口） */
  function handleNonLiveMessage(type: LotteryType, k: string) {
    const drawCompleted = type === 'hk' ? hkDrawCompleted : aocaiDrawCompleted
    const drawingStarted = type === 'hk' ? hkDrawingStarted : aocaiDrawingStarted

    if (drawCompleted) {
      // 开奖已完成，提取全部数据（含当期结果 + 下期信息）
      const parsed = parseWsKField(k, type)
      logDebug('WS', '开奖已完成，提取下期信息', type, {
        next_issue: parsed.next_issue,
        next_draw_date: parsed.next_draw_date,
        next_draw_time: parsed.next_draw_time,
      })
      mergeData(type, parsed)

      // 收到有效的下期开奖时间后，重置周期标志，为下一轮开奖做准备
      if (parsed.next_draw_time) {
        logDebug('WS', '已收到下期信息，重置开奖周期', type)
        if (type === 'hk') {
          hkDrawCompleted = false
          hkDrawingStarted = false
          hkDrawStartTime = 0
          hkDrawingMode.value = false
        } else {
          aocaiDrawCompleted = false
          aocaiDrawingStarted = false
          aocaiDrawStartTime = 0
          aocaiDrawingMode.value = false
        }
      }
    } else if (drawingStarted) {
      // 曾进入直播（k[0]===k[8]）但开奖未标记完成：
      // 可能因 WS 推送间隔 > 揭晓间隔，跳过了最后的 DRAWING 消息，
      // 直接收到 POST_DRAW。POST_DRAW 的 k 字段仍包含完整开奖号码。
      const parsed = parseWsKField(k, type)
      logDebug('WS', '收到POST_DRAW消息，补全开奖数据', type, {
        main_numbers: parsed.main_numbers,
        special_number: parsed.special_number,
      })
      mergeData(type, parsed)
      checkDrawComplete(type)
    } else {
      // drawingStarted === false：尚未进入直播，PRE_DRAW 消息。
      // WS 常驻连接模式下，提取全部数据（含当期结果 + 下期信息）。
      const parsed = parseWsKField(k, type)
      logDebug('WS', '收到PRE_DRAW消息', type, {
        current_issue: parsed.current_issue,
        main_numbers: parsed.main_numbers,
        next_issue: parsed.next_issue,
        next_draw_time: parsed.next_draw_time,
      })
      mergeData(type, parsed)
    }
  }

  /** k[0] === k[8]：开奖直播窗口（DRAWING） */
  function handleDrawingMessage(type: LotteryType, k: string) {
    if (type === 'hk') {
      if (!hkDrawingStarted) hkDrawStartTime = Date.now()
      hkDrawingStarted = true
      hkDrawingMode.value = true
    } else {
      if (!aocaiDrawingStarted) aocaiDrawStartTime = Date.now()
      aocaiDrawingStarted = true
      aocaiDrawingMode.value = true
    }

    const parsed = parseWsKField(k, type)
    logDebug('WS', '收到DRAWING消息', type, {
      current_issue: parsed.current_issue,
      main_numbers: parsed.main_numbers,
      special_number: parsed.special_number,
    })
    mergeData(type, parsed)
    checkDrawComplete(type)
  }

  /** 断线时是否处于重连窗口：开奖前 10 分钟 或 开奖开始后 10 分钟内；尚无数据时始终重连 */
  function isInReconnectWindow(type: LotteryType): boolean {
    // 重连条件：开奖前 10 分钟 或 开奖开始后 10 分钟内
    const RECONNECT_WINDOW_MS = 10 * 60 * 1000
    const currentData = type === 'hk' ? hkData.value : aocaiData.value
    const drawStartTime = type === 'hk' ? hkDrawStartTime : aocaiDrawStartTime

    let shouldReconnect = false
    if (currentData) {
      // 条件1：开奖前 10 分钟
      if (isNearNextDraw(currentData, RECONNECT_WINDOW_MS)) {
        shouldReconnect = true
      }
      // 条件2：开奖开始后 10 分钟内
      if (drawStartTime > 0 && Date.now() - drawStartTime <= RECONNECT_WINDOW_MS) {
        shouldReconnect = true
      }
    } else {
      // 尚无数据时始终重连（需要获取初始数据）
      shouldReconnect = true
    }
    return shouldReconnect
  }

  function connectWs(type: LotteryType, isReconnect = false) {
    const existingWs = type === 'hk' ? hkWs : aocaiWs
    // OPEN 或 CONNECTING 时均不重复建立连接
    if (existingWs && existingWs.readyState <= WebSocket.OPEN) return

    logDebug('WS', isReconnect ? '重连' : '新建连接', type)

    if (!isReconnect) {
      if (type === 'hk') {
        hkDrawCompleted = false
        hkDrawingStarted = false
        hkDrawStartTime = 0
      } else {
        aocaiDrawCompleted = false
        aocaiDrawingStarted = false
        aocaiDrawStartTime = 0
      }
    }

    const ws = new WebSocket(WS_ENDPOINTS[type])
    if (type === 'hk') hkWs = ws
    else aocaiWs = ws

    ws.onopen = () => {
      logDebug('WS', '已连接', type)
      if (type === 'hk') hkWsActive.value = true
      else aocaiWsActive.value = true
      // 建立连接后发送一条空消息，触发服务端开始推送数据
      ws.send('')
      // 每 5 秒发送一条空消息，保持数据推送
      const sendTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('')
        }
      }, 5000)
      if (type === 'hk') hkWsSendTimer = sendTimer
      else aocaiWsSendTimer = sendTimer

      // 记录初始时间，启动消息超时检测
      const now = Date.now()
      if (type === 'hk') hkLastMessageTime = now
      else aocaiLastMessageTime = now

      const timeoutTimer = setInterval(() => {
        const lastTime = type === 'hk' ? hkLastMessageTime : aocaiLastMessageTime
        const drawCompleted = type === 'hk' ? hkDrawCompleted : aocaiDrawCompleted
        if (drawCompleted) return
        // 仅在号码正在逐个揭晓时才触发超时重连：
        // - 至少有一个号码已揭晓为数字（排除临近开奖全是中文占位符的阶段）
        // - 且尚未全部揭晓完成
        const currentData = type === 'hk' ? hkData.value : aocaiData.value
        if (!currentData) return
        const allNums = [...(currentData.main_numbers ?? []), currentData.special_number ?? '']
        const hasRevealedDigit = allNums.some(isRevealedNumber)
        if (!hasRevealedDigit) return
        if (Date.now() - lastTime > WS_MESSAGE_TIMEOUT_MS) {
          logDebug('WS', '消息超时，触发重连', type, '距上次消息', Date.now() - lastTime, 'ms')
          ws.close() // 触发 onclose 中的重连逻辑
        }
      }, 5000)
      if (type === 'hk') hkMessageTimeoutTimer = timeoutTimer
      else aocaiMessageTimeoutTimer = timeoutTimer
    }

    ws.onmessage = (event) => {
      // 更新最后消息时间
      if (type === 'hk') hkLastMessageTime = Date.now()
      else aocaiLastMessageTime = Date.now()

      try {
        const msg = JSON.parse(event.data as string) as { k?: string }
        if (!msg.k) return
        const parts = msg.k.split(',')
        if (parts.length < 9) return

        if (parts[0] === parts[8]) handleDrawingMessage(type, msg.k)
        else handleNonLiveMessage(type, msg.k)
      } catch {
        // JSON 解析失败时静默忽略，等待下条消息
      }
    }

    ws.onerror = () => {
      logDebug('WS', '错误', type)
    }

    ws.onclose = () => {
      if (type === 'hk') {
        clearInterval(hkWsSendTimer)
        clearInterval(hkMessageTimeoutTimer)
        hkWs = null
        hkWsActive.value = false
      } else {
        clearInterval(aocaiWsSendTimer)
        clearInterval(aocaiMessageTimeoutTimer)
        aocaiWs = null
        aocaiWsActive.value = false
      }

      const shouldReconnect = isInReconnectWindow(type)
      if (shouldReconnect) {
        logDebug('WS', '断开，处于重连窗口，准备重连', type)
        const timer = setTimeout(() => connectWs(type, true), WS_RECONNECT_DELAY_MS)
        if (type === 'hk') hkWsReconnectTimer = timer
        else aocaiWsReconnectTimer = timer
      } else {
        logDebug('WS', '断开，不在重连窗口，不重连', type)
      }
    }
  }

  /** 立即连接港彩和澳彩两个 WS */
  function connectBoth() {
    connectWs('hk')
    connectWs('macau')
  }

  /** 手动重连指定彩票类型的 WS（作为全新连接，重置开奖周期状态） */
  function reconnect(type: LotteryType) {
    connectWs(type, false)
  }

  onUnmounted(() => {
    clearTimeout(hkWsReconnectTimer)
    clearTimeout(aocaiWsReconnectTimer)
    clearInterval(hkWsSendTimer)
    clearInterval(aocaiWsSendTimer)
    clearInterval(hkMessageTimeoutTimer)
    clearInterval(aocaiMessageTimeoutTimer)
    closeWs('hk')
    closeWs('macau')
  })

  return { hkWsActive, aocaiWsActive, connectBoth, reconnect }
}

// ==================== 异步组件 ====================

/** 直播播放器：仅在开奖直播期间加载，video.js 随此 chunk 异步载入，不影响首屏 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LiveStreamPlayer: any = defineAsyncComponent(() => import('@/components/live-stream-player'))

// ==================== 子组件 ====================

// 开奖结果卡片
export const LotteryResultPanel = defineComponent({
  props: {
    lotteryType: { type: String as () => LotteryType, required: true },
    liveData: { type: Object as () => LiveLotteryData | null, default: null },
    loading: { type: Boolean, default: false },
    drawingMode: { type: Boolean, default: false },
    showCountdown: { type: Boolean, default: true },
  },
  emits: ['enterDrawing'],
  setup(props, { emit }) {
    const router = useRouter()
    const countdown = ref('--:--:--')
    let timer: ReturnType<typeof setInterval> | undefined

    let lastLoggedDate = ''
    let lastLoggedTime = ''
    function getTargetDate(): Date | null {
      const d = props.liveData
      if (!d?.next_draw_date || !d?.next_draw_time) return null
      // 仅在数据变化时打印，避免每秒刷屏
      if (d.next_draw_date !== lastLoggedDate || d.next_draw_time !== lastLoggedTime) {
        lastLoggedDate = d.next_draw_date
        lastLoggedTime = d.next_draw_time
        logDebug('Countdown', '解析开奖时间', {
          type: props.lotteryType,
          next_draw_date: d.next_draw_date,
          next_draw_time: d.next_draw_time,
        })
      }
      return parseNextDrawDatetime(d.next_draw_date, d.next_draw_time)
    }

    function updateCountdown() {
      // drawingMode 由父组件控制：进入直播时设 true，收到下期信息或超时后清除
      if (props.drawingMode) {
        countdown.value = 'DRAWING'
        return
      }
      const target = getTargetDate()
      if (!target) {
        countdown.value = '--:--:--'
        return
      }
      const diff = target.getTime() - Date.now()
      if (diff <= 0) {
        // 倒计时归零，通知父组件进入 drawingMode
        emit('enterDrawing')
        countdown.value = 'DRAWING'
        return
      }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      countdown.value = [h, m, s].map((n) => String(n).padStart(2, '0')).join(':')
    }

    onMounted(() => {
      updateCountdown()
      timer = setInterval(updateCountdown, 1000)
    })

    onUnmounted(() => {
      if (timer) clearInterval(timer)
    })

    watch(
      () => props.liveData,
      () => updateCountdown(),
    )

    return () => {
      const d = props.liveData
      if (props.loading || !d) {
        return (
          <div class="px-3 pt-3 pb-4 flex items-center justify-center h-[80px]">
            <div class="w-5 h-5 rounded-full border-2 border-gray-300 border-t-green-500 animate-spin" />
          </div>
        )
      }
      const { balls, specialBall } = parseLiveBalls(d)
      const nextDrawDisplay = [
        `第${normalizeIssue(d.next_issue, d.next_draw_date)}期`,
        d.next_draw_date,
        d.next_draw_time,
        // 如果是「星期」开头的，则去掉它
        '星期' + d.next_draw_weekday?.replace(/^星期/, ''),
      ].join(' ')

      return (
        <>
          {/* 顶部行：期号 | 倒计时 | 查看历史 */}
          <div class="flex items-center justify-between mb-3 text-3 min-[380px]:text-3.5 min-[400px]:text-4">
            <span class="text-gray-800">
              第<span class="text-green-600">
                {normalizeIssue(d.current_issue, d.next_draw_date)}
              </span>期
            </span>
            {props.showCountdown && (
              <span class="text-red-500">
                {countdown.value === 'DRAWING' ? (
                  <span class="font-bold">开奖中</span>
                ) : (
                  <>
                    距下期开奖：<span class="font-mono">{countdown.value}</span>
                  </>
                )}
              </span>
            )}
            <button
              class="text-green-600 bg-transparent font-size-inherit border-none cursor-pointer p-0"
              onClick={() => router.push(`/history?type=${props.lotteryType}`)}
            >
              查看历史记录
            </button>
          </div>

          {/* 彩球行 */}
          <div class="mb-3">
            <LotteryBallRow
              balls={balls}
              specialBall={specialBall}
              useAlgorithm
              lotteryDate={getTodayDateStr()}
            />
          </div>

          {/* 下次开奖信息 */}
          <div class="text-center text-4 text-red-500">{nextDrawDisplay}</div>
        </>
      )
    }
  },
})

const ExternalLinkButton = defineComponent({
  props: {
    link: { type: Object as () => ExternalLink, required: true },
  },
  setup(props) {
    const contaienrRef = ref<HTMLAnchorElement | null>(null)

    onMounted(() => {
      // 当文字太长出现换行的时候，尝试缩小font-size，进入循环，直到不换行或者缩小到10px为止
      const el = contaienrRef.value
      if (!el) return

      const checkAndShrink = () => {
        const parentWidth = el.parentElement?.offsetWidth ?? 0
        const offsetWidth = el.offsetWidth // 内容实际宽度（包括超出部分）
        const isWrapped = offsetWidth > parentWidth - 14
        if (isWrapped) {
          const style = globalThis.getComputedStyle(el)
          const fontSize = Number.parseFloat(style.fontSize)
          if (fontSize > 10) {
            el.style.fontSize = `${fontSize - 1}px`
            // 等待 DOM 更新后再检查
            requestAnimationFrame(checkAndShrink)
          }
        }
      }

      // 初始检查
      checkAndShrink()

      // 当属性变化时再检查一次（文本变更等）
      const observer = new MutationObserver(checkAndShrink)
      observer.observe(el, { attributes: true, characterData: true, subtree: true })
    })

    return () => (
      <a
        href={normalizeExternalUrl(props.link.link_url)}
        target="_blank"
        rel="noopener noreferrer"
        class="whitespace-nowrap flex items-center justify-center font-size-4 font-400 text-gray-700 decoration-none py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors block text-center truncate overflow-hidden"
      >
        <span ref={contaienrRef}>{props.link.link_title}</span>
      </a>
    )
  },
})

// 外部链接（4列）
const ExternalLinksPanel = defineComponent({
  props: {
    links: { type: Array as () => ExternalLink[], default: () => [] },
  },
  setup(props) {
    return () => (
      <div class="grid grid-cols-4 gap-1">
        {props.links.map((link) => (
          <ExternalLinkButton key={link.id} link={link} />
        ))}
      </div>
    )
  },
})

// 百度 Logo base64
const BAIDU_LOGO =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAAAUCAYAAAA0nZtFAAADrElEQVR4nM2YW6hVVRSGv3EyPaES5qWL2UUIRQztZj1EUqfsgpeXMsgguzxE9CBlQYGWUA9hRCFFPphEmV2g20NBnlAfgl5UQtEulN1EOVBBaSc66NfDmrtmu932Wnt7jvTDYu41xj/GHGPOscacbGgBdaK6WR3dSj+SUPvU79Vlx+Fjjfpw9j5OfUVd1K3DpyzwXrdBdQt1dFqQnV3az1KPqWsz2Wkpn0er7EeVyBemcbHaFxEfdxNcGdQJQLsK2Atcrz4BHCrhDEbEhhbyh4AAWukqEc0CdQwwmOneioilSTcNuASYAvwKfAXsjAg7mVSdCezrJuAMAxFxegu/nwECWzLVaGAB8AVFzK2wKSJeb1UhQ/yzGF8Ca9SbgMeAeS34B9VngXURMVgzmQbuBzZ2aNPAvzZBPQP4EDgK7KbYtAYaeY5tkucYWz6T/qjuUCerG6yH3er0WpnozGRzTx1+DX/j1Z3qUNq8Zv1x95CX0vM8cEsmPwzsoNiBs4HLgKlJNxvYrs6LiIP10xkWXATMAe6KiA9GZAb1vqYKeE2d2MTpUVeoRzJefw3fw1ohyefFbXSdVYjaBzxIseP7gPnA4xnvBeBFiuaU41xgG7AE+Iii9/Sp10XEFk4Q0vVgvJb29pPTeLd6bTtfPeoqoB+4EZgEfErRkScnzn6Ko2w6cEA9pO5TfwK+ASZGRD+wPvN7e0UOvWn8vYI3XBgCtgLfVTLVa9RX1e3q2+ql6tPZJ7Au8aaUNNPepL86k31dMeeVibewHe9/A3VTltydSdZjcQPM8UtmhWomb3v8qjck3lUjnUun6CmR56U8KY0T+O9Fbrza08Rrtm+Fs9I4UBlhB1DnqH918dzR8FF27OYl37iMzW3BOwmYBewBLi+xb4XpwDGKHjScCIoGupnixlqFScBKygujgDo7K/+h1FcWqYvVaeop6tT0foXaq+7NbFZX+H9D/bZejvWhzk3z31qTf0HeFqrI/VmCn6vnlfDGqBsz7mH1zArfP6jv1gm6EwzHgrQrlRXAkfR7BrBbXaleqI5KlXIzsAtYntmtbndTVc+nuOVuqxP0iUZZDyEi9qi3AW8CY4BxwNr0HKXoH81YHxHPVMy5NI1bOw+3Np5UH6jB620WlC4IQES8r84HXqaokgaaF+M3YFVEPFcjiOXAnoio0/S6xc/AgRq8cV15T5/IMvUdi3+z/lAH1E/UR9TJ1V7+9rVUvberQKp9z1B3qQtq8s9J/CUN2V9qG09/ZEWOoAAAAABJRU5ErkJggg=='

// 关闭图标 base64
const CLOSE_ICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAnElEQVR4nKXTMQ7DIAxG4WePuUcy9zY5TTKw527dew/WP0OpVCkGpyorfA8hCyQtkjZJEzeXpKmZxYEVeAD7nUg7szezOnAAL2DOIl94buZwM6tAySIBLmZWHSCL9DCAZbe0rRBfAp0IPRwGggg9DOBR4Jd1CQRPGE7HE1xIRuwjbGY1G7GP8OfQKGIZzp5okjbeH2OIO5En/37nEzqypn0SJnNTAAAAAElFTkSuQmCC'

// 伪百度关键词盒子（自拉数据，type: 1=首页 2=详细页）
export const FakeKeywordsBox = defineComponent({
  props: {
    type: { type: Number, default: 1 },
  },
  setup(props) {
    const visible = ref(true)
    const keywords = ref<KeywordRanking[]>([])
    const wideIndexes = ref<Set<number>>(new Set())

    const itemRefs: HTMLAnchorElement[] = []
    const setItemRef = (el: HTMLAnchorElement | null, index: number) => {
      if (el) itemRefs[index] = el
    }

    onMounted(async () => {
      const res = await getApiV1KeywordRankings({ query: { type: props.type } })
      const raw = res.data?.data ?? []
      keywords.value = [...raw].sort((a, b) => (a.ranking ?? 0) - (b.ranking ?? 0))
      // 等 DOM 更新后检测溢出
      setTimeout(() => {
        const wide = new Set<number>()
        itemRefs.forEach((el, i) => {
          if (el && el.scrollWidth > el.clientWidth) wide.add(i)
        })
        wideIndexes.value = wide
      }, 0)
    })

    return () => {
      if (!visible.value || keywords.value.length === 0) return null
      return (
        <div class="bg-white px-[0.12rem] pb-[0.12rem]">
          {/* 关键词网格 — 与首页 website-body 同款胶囊按钮，2 列 */}
          <div class="grid grid-cols-2 gap-x-[0.2rem] gap-y-[0.16rem] pt-[0.16rem]">
            {keywords.value.map((item, index) => (
              <a
                key={`${item.keyword}-${item.link}`}
                ref={(el) => setItemRef(el as HTMLAnchorElement | null, index)}
                href={item.link}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  const bridge = (globalThis as unknown as {
                    AndroidBridge?: { openCustomTabs?: (url: string) => void }
                  }).AndroidBridge
                  if (bridge?.openCustomTabs && item.link) {
                    e.preventDefault()
                    bridge.openCustomTabs(item.link)
                  }
                }}
                class="block text-center bg-[#f8f8f8] text-[#444] text-[0.28rem] h-[0.6rem] leading-[0.6rem] rounded-[0.08rem] overflow-hidden whitespace-nowrap text-ellipsis decoration-none px-[0.1rem]"
              >
                {item.keyword}
              </a>
            ))}
          </div>
          {/* 底部：百度 Logo + 关闭按钮 */}
          {/* <div class="flex items-center justify-between px-2 py-1.5 border-t border-gray-100">
            <img src={BAIDU_LOGO} alt="百度" class="h-4 w-auto" />
            <img
              src={CLOSE_ICON}
              alt="关闭"
              class="h-4 w-4 cursor-pointer opacity-50 hover:opacity-80"
              onClick={() => (visible.value = false)}
            />
          </div> */}
        </div>
      )
    }
  },
})

// ==================== 导出组件 ====================

/** 手动 展开/隐藏 直播窗口的按钮 */
export const useManualToggleLivePlayerHook = () => {
  const manualShowLivePlayer = ref<boolean | undefined>(undefined)
  const manualToggleLivePlayer = () => {
    if (manualShowLivePlayer.value === undefined) {
      manualShowLivePlayer.value = true
    } else {
      manualShowLivePlayer.value = !manualShowLivePlayer.value
    }
  }

  return {
    manualShowLivePlayer,
    manualToggleLivePlayer,
  }
}

/**
 * 港彩/澳彩 Tab 容器
 *
 * @prop showResult 是否显示开奖结果面板，默认 true
 * @prop showDrawDate 是否在 Tab 上显示开奖日期，默认 true
 */
export const LotteryTabs = defineComponent({
  props: {
    showResult: { type: Boolean, default: true },
    showDrawDate: { type: Boolean, default: true },
    showExternalLinksPanel: { type: Boolean, default: true },
    showKeywords: { type: Boolean, default: true },
    showTabSwitcher: { type: Boolean, default: true },
    showLivePlayer: { type: Boolean, default: true },
    showCountdown: { type: Boolean, default: true },
  },
  setup(props) {
    const regionStore = useRegionStore()

    const hkData = ref<LiveLotteryData | null>(null)
    const aocaiData = ref<LiveLotteryData | null>(null)
    const externalLinks = ref<ExternalLink[]>([])
    const loading = ref(false)
    const hkDrawingMode = ref(false)
    const aocaiDrawingMode = ref(false)

    const { connectBoth } = useWsLiveData(hkData, aocaiData, hkDrawingMode, aocaiDrawingMode)

    onMounted(async () => {
      loading.value = true
      // 仅保留外部链接的 API 调用
      const linksRes = await getApiV1ExternalLinksList({
        query: { page: 1, size: 999, status: 1 },
      })
      externalLinks.value = linksRes.data?.data?.list ?? []
      // 立即连接两个 WS（数据全部来自 WS）
      connectBoth()
    })

    // 首条 WS 消息到达时取消 loading
    watch([hkData, aocaiData], () => {
      if (loading.value && (hkData.value || aocaiData.value)) {
        loading.value = false
      }
    })

    // 数据变化时锁存 drawingMode（单向：仅 false → true）
    watch(hkData, (d) => {
      if (d && !hkDrawingMode.value && shouldShowLivePlayer(d)) hkDrawingMode.value = true
    })
    watch(aocaiData, (d) => {
      if (d && !aocaiDrawingMode.value && shouldShowLivePlayer(d)) aocaiDrawingMode.value = true
    })

    // 倒计时归零时由 LotteryResultPanel emit 触发
    function handleEnterDrawing(type: LotteryType) {
      if (type === 'hk') hkDrawingMode.value = true
      else aocaiDrawingMode.value = true
    }

    // 澳彩在左，港彩在右
    const tabs: Array<{ type: LotteryType; label: string }> = [
      { type: 'macau', label: '澳彩' },
      { type: 'hk', label: '港彩' },
    ]

    const { manualShowLivePlayer, manualToggleLivePlayer } = useManualToggleLivePlayerHook()

    return () => {
      const currentData = regionStore.lotteryType === 'hk' ? hkData.value : aocaiData.value
      const currentDrawingMode =
        regionStore.lotteryType === 'hk' ? hkDrawingMode.value : aocaiDrawingMode.value
      const lotteryTypeLabel = regionStore.lotteryType === 'hk' ? '港彩' : '澳彩'
      const filteredLinks = externalLinks.value
        .filter((l) => l.lottery_type === lotteryTypeLabel)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

      const getLiveStreamVNode = () => <LiveStreamPlayer lotteryType={regionStore.lotteryType} />
      const getTogglePlayerButton = (label: string) => (
        <button
          type="button"
          class="btn-reset relative flex items-center justify-center w-33 mb-[-0.5rem]! mx-auto aspect-ratio-[10/3]"
          onClick={manualToggleLivePlayer}
        >
          <span class="z-1 text-4 text-white">{label}</span>
          <svg viewBox="0 0 10 3" class="w-full h-full absolute top-0 left-0">
            <path class="fill-green-500" d="M0,0 L10,0 L8,3 L2,3 z" />
          </svg>
        </button>
      )

      return (
        <>
          <div class="flex flex-col gap-2 pb-2">
            {/* Tab 切换栏 */}
            {props.showTabSwitcher && (
              <div class="flex px-2 pt-2 gap-2 bg-[#f5f5f5]">
                {tabs.map((tab) => {
                  const isActive = regionStore.lotteryType === tab.type
                  const tabData = tab.type === 'hk' ? hkData.value : aocaiData.value
                  const nextDrawDate = tabData?.next_draw_date
                    ? formatNextDrawDate(tabData.next_draw_date)
                    : '--'
                  return (
                    <button
                      key={tab.type}
                      class={[
                        'flex-1 py-1 flex flex-col items-center gap-0.5 border-none rounded-t-md cursor-pointer transition-colors',
                        isActive ? 'bg-green-500 text-white' : 'bg-white text-gray-700',
                      ]}
                      onClick={() => regionStore.setLotteryType(tab.type)}
                    >
                      <span class="font-size-5 font-bold">{tab.label}</span>
                      {props.showDrawDate && (
                        <span class={['text-md', isActive ? 'text-green-100' : 'text-gray-500']}>
                          {nextDrawDate}开奖
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
            {/* 直播播放器：开奖直播期间显示，跟随 drawingMode 标志 */}
            {props.showLivePlayer &&
              (manualShowLivePlayer.value === undefined
                ? currentDrawingMode && getLiveStreamVNode()
                : manualShowLivePlayer.value && getLiveStreamVNode())}
            {props.showLivePlayer &&
              getTogglePlayerButton(
                (manualShowLivePlayer.value ?? currentDrawingMode) ? '收起直播' : '展开直播',
              )}
            {/* 内容区 */}
            <div class="mx-2 flex flex-col gap-2">
              {props.showResult && (
                <div class="border border-[#07c160] border-solid rounded-md overflow-hidden py-2 px-2">
                  <LotteryResultPanel
                    lotteryType={regionStore.lotteryType}
                    liveData={currentData}
                    loading={loading.value}
                    drawingMode={currentDrawingMode}
                    showCountdown={props.showCountdown}
                    onEnterDrawing={() => handleEnterDrawing(regionStore.lotteryType)}
                  />
                </div>
              )}
              {props.showExternalLinksPanel && <ExternalLinksPanel links={filteredLinks} />}
            </div>
          </div>
          {props.showKeywords && <FakeKeywordsBox type={1} />}
        </>
      )
    }
  },
})
const _placeholder = defineComponent({ setup: () => () => <div /> })
export default _placeholder
